import {
	Button,
	type ColumnDef,
	DataTable,
	FilterBuilder,
	type FilterField,
	SearchInput,
	useFilterBuilder,
	useUrlState,
} from "@monbits/ui";
import { Copy, DollarSign, Trash2 } from "lucide-react";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ConfirmationDialog } from "src/components/confirmation-dialog";
import DrawerForm from "src/components/drawer-form/drawer-form";
import PaymentLinkForm from "src/components/payment-link-create/payment-link-create";
import PaymentLinkDescCard from "src/components/payment-link-desc-card/payment-link-desc-card";
import TimeLabel from "src/components/time-label/time-label";
import paymentLinkQueriers from "src/queries/payment-link-queries";
import {
	CURRENCY_SYMBOL,
	type PaymentLink,
	type PaymentLinkParams,
} from "src/types";
import copyToClipboard from "src/utils/copy-to-clipboard";
import { fiatOptions } from "src/utils/options";

const FILTER_FIELDS: FilterField[] = [
	{
		id: "currency",
		label: "Currency",
		type: "select",
		icon: <DollarSign className="h-4 w-4" />,
		options: fiatOptions,
	},
];

const PaymentLinksPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const search = searchParams.get("search") || "";

	const { filters, setFilters } = useFilterBuilder({
		fields: FILTER_FIELDS,
		searchParams,
		setSearchParams,
	});

	const [pagination, setPagination] = useUrlState({
		page: { type: "number", default: 1 },
		limit: { type: "number", default: 20 },
	});

	const page = pagination.page;
	const limit = pagination.limit;
	const setPage = (newPage: number) => setPagination({ page: newPage });
	const setLimit = (newLimit: number) =>
		setPagination({ limit: newLimit, page: 1 });

	// Convert FilterBuilder filters to API params
	const apiFilters = React.useMemo(() => {
		const result: {
			currency?: string;
		} = {};

		for (const filter of filters) {
			if (filter.operator === "is" && filter.value) {
				if (filter.field === "currency") {
					result.currency = filter.value;
				}
			}
		}

		return result;
	}, [filters]);

	const listPaymentLinks = paymentLinkQueriers.listPaymentLinks({
		search,
		currency: apiFilters.currency,
		limit,
		offset: (page - 1) * limit,
	});

	const createPaymentLink = paymentLinkQueriers.createPaymentLink();
	const deletePaymentLink = paymentLinkQueriers.deletePaymentLink();
	const [isFormOpen, setFormOpen] = React.useState<boolean>(false);
	const [openedCard, changeOpenedCard] = React.useState<PaymentLink | null>(
		null,
	);
	const [isLinkDrawerOpen, setIsLinkDrawerOpen] = React.useState(false);
	const paymentLinks = listPaymentLinks.data?.results ?? [];
	const totalCount = listPaymentLinks.data?.totalCount ?? 0;
	const [isFormSubmitting, setIsFormSubmitting] =
		React.useState<boolean>(false);
	const [linkToDelete, setLinkToDelete] = React.useState<PaymentLink | null>(
		null,
	);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

	const isLoading =
		listPaymentLinks.isLoading ||
		createPaymentLink.isPending ||
		listPaymentLinks.isFetching ||
		deletePaymentLink.isPending;

	const openNotification = React.useCallback(
		(title: string, description: string) => {
			toast.success(title, {
				description,
			});
		},
		[],
	);

	const deleteSelectedLink = async (paymentLinkId: string) => {
		try {
			setIsFormSubmitting(true);
			await deletePaymentLink.mutateAsync(paymentLinkId);
			toast.success("Payment link deleted", {
				description: "The payment link has been removed.",
			});
			setFormOpen(false);
		} catch (error) {
			// 400 errors are handled by apiRequest interceptor
			// Only log here, don't show duplicate toast
			console.error("Delete payment link error:", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const uploadCreatedPaymentLink = async (value: PaymentLinkParams) => {
		try {
			setIsFormSubmitting(true);
			await createPaymentLink.mutateAsync(value);
			toast.success("Payment link created", {
				description: "You can now share this link with your customers.",
			});
			setFormOpen(false);
		} catch (error) {
			// 400 errors are handled by apiRequest interceptor
			// Only log here, don't show duplicate toast
			console.error("Create payment link error:", error);
		} finally {
			setIsFormSubmitting(false);
		}
	};

	const handleViewLink = React.useCallback((link: PaymentLink) => {
		changeOpenedCard(link);
		setIsLinkDrawerOpen(true);
	}, []);

	const columns: ColumnDef<PaymentLink>[] = React.useMemo(
		() => [
			{
				id: "name",
				header: "Link",
				cell: (row) => (
					<div className="min-w-0">
						<div className="font-medium truncate">{row.name || "Untitled"}</div>
						{row.description && (
							<div className="text-xs text-muted-foreground truncate max-w-[200px]">
								{row.description}
							</div>
						)}
					</div>
				),
			},
			{
				id: "price",
				header: "Price",
				cell: (row) => (
					<span className="text-sm font-semibold font-mono">
						{CURRENCY_SYMBOL[row.currency]}
						{row.price}
					</span>
				),
			},
			{
				id: "url",
				header: "URL",
				cell: (row) => (
					<button
						type="button"
						className="group flex items-center gap-2 max-w-[240px]"
						onClick={(e) => {
							e.stopPropagation();
							copyToClipboard(row.url, openNotification);
						}}
					>
						<span className="text-sm text-muted-foreground font-mono truncate">
							{row.url.replace(/^https?:\/\//, "")}
						</span>
						<Copy className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
					</button>
				),
			},
			{
				id: "createdAt",
				header: "Created",
				cell: (row) => <TimeLabel time={row.createdAt} />,
			},
			{
				id: "actions",
				header: "",
				cell: (row) => (
					<div className="flex justify-end">
						<Button
							variant="ghost"
							size="icon"
							aria-label="Delete payment link"
							className="text-muted-foreground hover:text-destructive"
							onClick={(e) => {
								e.stopPropagation();
								setLinkToDelete(row);
								setIsDeleteDialogOpen(true);
							}}
						>
							<Trash2 className="h-4 w-4" />
						</Button>
					</div>
				),
			},
		],
		[openNotification],
	);

	const hasActiveFilters = search || filters.length > 0;

	return (
		<div className="space-y-6 p-4 sm:p-6">
			<h1 className="sr-only">Payment Links</h1>

			<div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-3">
					<span aria-hidden className="h-5 w-[3px] rounded-full bg-primary" />
					<p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
						Payment Links · Shareable checkout URLs
					</p>
				</div>
				<Button onClick={() => setFormOpen(true)}>New Payment Link</Button>
			</div>

			<div className="flex gap-4 flex-wrap items-center">
				<SearchInput
					placeholder="Search links..."
					className="w-full sm:w-[300px]"
				/>
				<FilterBuilder
					fields={FILTER_FIELDS}
					filters={filters}
					onFiltersChange={setFilters}
				/>
			</div>

			<DataTable
				columns={columns}
				data={paymentLinks}
				isLoading={isLoading}
				onRowClick={handleViewLink}
				getRowId={(row) => row.id}
				emptyMessage={
					hasActiveFilters ? "No payment links found" : "No payment links yet"
				}
				emptyDescription={
					hasActiveFilters
						? "Try adjusting your search or filter criteria"
						: "Create reusable links to share with your customers. Each link can be used for multiple payments."
				}
				emptyAction={
					hasActiveFilters ? undefined : (
						<Button onClick={() => setFormOpen(true)}>
							Create your first link
						</Button>
					)
				}
				pagination={
					totalCount > 0
						? {
								page,
								pageSize: limit,
								totalCount,
								onPageChange: setPage,
								onPageSizeChange: setLimit,
							}
						: undefined
				}
			/>

			<DrawerForm
				title="Create a link"
				description="Create a payment link that you can share with your customers."
				isOpen={isFormOpen}
				onClose={() => setFormOpen(false)}
				onSubmit={() => {
					const form = document.querySelector("form");
					if (form) {
						form.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={isFormSubmitting}
				submitText="Create Link"
				width={512}
			>
				<PaymentLinkForm
					onFinish={uploadCreatedPaymentLink}
					isFormSubmitting={isFormSubmitting}
				/>
			</DrawerForm>
			<DrawerForm
				title={openedCard?.name || "Link Details"}
				description="View and manage this payment link"
				isOpen={isLinkDrawerOpen}
				onClose={() => setIsLinkDrawerOpen(false)}
				width={512}
			>
				{openedCard && (
					<PaymentLinkDescCard
						data={openedCard}
						openNotificationFunc={openNotification}
					/>
				)}
			</DrawerForm>

			<ConfirmationDialog
				open={isDeleteDialogOpen}
				onOpenChange={setIsDeleteDialogOpen}
				onConfirm={() => {
					if (linkToDelete) {
						return deleteSelectedLink(linkToDelete.id);
					}
				}}
				title="Delete payment link"
				description="Are you sure you want to delete this payment link? This action cannot be undone."
				confirmText="Delete"
				variant="destructive"
			/>
		</div>
	);
};

export default PaymentLinksPage;
